import { ProxyService } from './proxy.service';
import { LocTrackingService } from './location-tracking.service';
import { Injectable, OnDestroy } from '@angular/core';
import { LocationsEntity, UserOrderInfo, IAppState, Actions, UnitLocation, Coordinates, LocationsModel, DistanceMatrixEntity, PickupType } from '@core/redux/stores';
import { select, NgRedux } from '@angular-redux/store';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import * as _ from 'lodash-es';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { LoggingService } from './logging.service';
import { AuthTrackingService } from './authTracking.service';
import { map } from 'rxjs/operators';
import { ConfirmModalService } from './confirm-modal.service';
import { ModalService } from './modal.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LocationConstants } from '@app/features/location/constants';

@Injectable()
export class LocationCoreService implements OnDestroy {

  @select(appState => appState.submittedOrder) submittedOrder: Observable<UserOrderInfo>;
  @select((appState) => appState.locationSelected) locationSelected: Observable<LocationsEntity>;
  private subscriptions = new Subscription();

  private submittedOrderLocation: LocationsEntity;
  private menuloadedSubject = new BehaviorSubject<boolean>(false);
  public isMenuLoaded$ = this.menuloadedSubject.asObservable();
  private selectedLocation: LocationsEntity;
  pickupTypeMetaData: any[];

  constructor(private locationTrackingService: LocTrackingService,
    private proxy: ProxyService,
    private ngRedux: NgRedux<IAppState>,
    private logger: LoggingService,
    private authTrackingService: AuthTrackingService,
    private confirmModalService: ConfirmModalService,
    private modalService: ModalService,
    private router: Router,
    private activatedRoute: ActivatedRoute) {
    this.selectedLocation = null;
    this.submittedOrderLocation = null;
    this.subscriptions.add(this.submittedOrder.subscribe((data) => {
      if (data) {
        this.submittedOrderLocation = _.cloneDeep(data).location;
      } else {
        this.submittedOrderLocation = null;
      }
    }));
    this.subscriptions.add(this.locationSelected.subscribe((data) => {
      if (data) {
        this.selectedLocation = _.cloneDeep(data);
      } else {
        this.selectedLocation = null;
      }
    }));
    this.subscriptions.add(
      this.confirmModalService.btnEvent.subscribe(async (data: string) => {
        if (data === 'selectPickupModal') {
          this.showModal('app-select-pickup');
        } else if (data === 'menuFlowGuard') {
          return this.sendAssumedLocation();
        } else if (data === 'locationCancel') {
          return;
        }
      })
    );
    this.pickupTypeMetaData = LocationConstants.PickupTypeMetaDataList;
  }
  private showModal(name: string) {
    this.modalService.setModalSelector(name);
    this.modalService.showModal();
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.selectedLocation = null;
    this.submittedOrderLocation = null;
  }
  private sendAssumedLocation() {
      return this.getAssumedLocation().then(
        (success) => {
          if (success && success.unit) {
            this.router.navigate(['/locations/' + success.unit + '/menu'], { queryParams: this.activatedRoute.snapshot.queryParams });
            return false;
          } else {
            this.router.navigate(['/locations'],{ queryParams: this.activatedRoute.snapshot.queryParams });
            return false;
          }          
        }
      ).catch((err) => {
        this.router.navigate(['/locations'],{ queryParams: this.activatedRoute.snapshot.queryParams });
        return true;
      });
  }
  
  async getAssumedLocation(ignoreOnlineOrderingAndClosedLocations = false): Promise<LocationsEntity> {
    // 1 Get location from Cart
    if (this.selectedLocation && !this.selectedLocation?.isAutomaticDeliveryLocation) {
      return this.selectedLocation;
    }
    let assumedLocation = null;
    // 2 Get Location from location services
    const locationByLatLng = await this.getLocationByLatLong();
    let lastOrderLocation: LocationsEntity = null;
    if(!locationByLatLng && this.submittedOrderLocation?.unit) {
      const url = ApiRoutes.UNIT_LOCATION + '?q=' + this.submittedOrderLocation?.unit;
      const response = await this.proxy.httpGet(url).toPromise();
      const data = <UnitLocation>response.body.valueOf();
      if (data.locations && data.locations[0]?.capabilities?.indexOf('Mobile-Order') > -1 && data.locations[0].openStatus !== 'Closed' || ignoreOnlineOrderingAndClosedLocations) {
        lastOrderLocation = data.locations[0];
      }
    }
    if (locationByLatLng) {
      assumedLocation = locationByLatLng;
    } else if (lastOrderLocation) { // 3 Get location from Last submitted order
      lastOrderLocation.isAssumedLocation = true;
      assumedLocation = _.cloneDeep(lastOrderLocation);
    } else if (this.authTrackingService.isLoggedIn() && this.locationTrackingService.getZipCode()) {
      // 4 Get location from zipcode - if the user is signed in
      const locationByZip = await this.getLocationByZip(this.locationTrackingService.getZipCode(), ignoreOnlineOrderingAndClosedLocations);
      if (locationByZip) {
        locationByZip.isAssumedLocation = true;
        assumedLocation = locationByZip;
      }
    }  if (!assumedLocation) { // 5 Get location based on IP address
      const locationByIP = await this.getLocationByIP(ignoreOnlineOrderingAndClosedLocations);
      if (locationByIP) {
        locationByIP.isAssumedLocation = true;
        assumedLocation = locationByIP;
      }
    }
    if (assumedLocation) {
      assumedLocation.isAssumedLocation = true;
      this.ngRedux.dispatch({ type: Actions.CLEARLOATIONSELECTED });
      this.ngRedux.dispatch({ type: Actions.SETLOCATIONSELECTED, data: assumedLocation });
    }
    return assumedLocation;
  }

  public setMenuLoadStatus(status: boolean) {
    this.menuloadedSubject.next(status);
  }
  private async getLocationByLatLong() {
    const lat = this.locationTrackingService.getLatitude();
    const lng = this.locationTrackingService.getLongitude();
    if (lat && lng) {
      const unitClosesttoLatLng = await this.getLocationByCoordinates(lat, lng);
      if (unitClosesttoLatLng && unitClosesttoLatLng.capabilities.indexOf('Mobile-Order') > -1) {
        unitClosesttoLatLng.isAssumedLocation = true;
        return unitClosesttoLatLng;
      }
    }
    return null;
  }
  public getLatLng(): Observable<Coordinates> {
    const lat = this.locationTrackingService.getLatitude();
    const lng = this.locationTrackingService.getLongitude();
    const zipCode = this.locationTrackingService.getZipCode();
    if (lat && lng) {
      return new Observable<Coordinates>(observer => {
        observer.next({ latitude: Number(lat), longitude: Number(lng) } as Coordinates);
        observer.complete();
      });
    } else if (zipCode) {
      return this.proxy.httpGet(`${ApiRoutes.GEO_COORDINATES}?zipcode=${zipCode}`).pipe(map(
        (success) => {
          const response = <Coordinates>success.body.valueOf();
          return ({ latitude: response.latitude, longitude: response.longitude } as Coordinates);
        }, err => {
          return this.getCoordinatesByIP();
        }));
    } else {
      return this.getCoordinatesByIP();
    }
  }
  private getCoordinatesByIP(): Observable<Coordinates> {
    return this.proxy.httpGet(`${ApiRoutes.GEO_COORDINATES_BY_IP}`).pipe(map(
      (success) => {
        const response = <Coordinates>success.body.valueOf();
        return ({ latitude: response.latitude, longitude: response.longitude } as Coordinates);
      }, err => {
        return <Coordinates>{ latitude: null, longitude: null };
      }));
  }

  async getLocationByIP(ignoreOnlineOrderingAndClosedLocations = false): Promise<LocationsEntity> {
    return await this.proxy.httpGet(`${ApiRoutes.GEO_COORDINATES_BY_IP}`).toPromise().then(
      (success) => {
        const response = <any>success.body.valueOf();
        if (response.latitude && response.longitude) {
          return this.getLocationByCoordinates(response.latitude, response.longitude, ignoreOnlineOrderingAndClosedLocations);
        }
        return null;
      },
      (err) => {
        this.logger.logError(err);
        return null;
      }
    ) as Promise<LocationsEntity>;
  }

  async getLocationByCoordinates(lat: string, lon: string, ignoreOnlineOrderingAndClosedLocations = false): Promise<LocationsEntity> {
    const query = ApiRoutes.REVERSE_GEOCODE + '?lat=' + lat + '&lng=' + lon;
    return await this.proxy.httpGet(query).toPromise()
      .then(
        (success) => {
          let assumedLocation = null;
          const resp = <any>success.body.valueOf();
          if (resp.locations && resp.locations.length > 0) {
            resp.locations.forEach(location => {
              if (!assumedLocation) {
                if (location.capabilities.indexOf('Mobile-Order') > -1 && location?.openStatus !== 'Closed' || ignoreOnlineOrderingAndClosedLocations) {
                  assumedLocation = location;
                }
              }
            });
          }
          return assumedLocation;
        },
        (error) => {
          this.logger.logError(error);
          return null;
        }
      ) as Promise<LocationsEntity>;
  }

  async getLocationByZip(zipCode: string, ignoreOnlineOrderingAndClosedLocations = false): Promise<LocationsEntity> {
    return await this.proxy.httpGet(`${ApiRoutes.LOCATIONS}?q=${zipCode}`).toPromise()
      .then(
        (success) => {
          let assumedLocation = null;
          const resp = <any>success.body.valueOf();
          if (resp.locations && resp.locations.length > 0) {
            resp.locations.forEach(location => {
              if (!assumedLocation) {
                if (location.capabilities.indexOf('Mobile-Order') > -1 && location?.openStatus !== 'Closed' || ignoreOnlineOrderingAndClosedLocations) {
                  assumedLocation = location;
                }
              }
            });
            return assumedLocation;
          }
          return null;
        },
        (error) => {
          this.logger.logError(error);
          return null;
        }
      );
  }
  public checkNearestLocationAvailability(source: string, defaultCallBack) {
    this.getAssumedLocation().then(location => {
      if (location && location.unit && location.capabilities?.indexOf('Mobile-Order') > -1) {
        defaultCallBack();
      } else {
        this.fetchLocationAndExecuteCallback(source, defaultCallBack);
      }
    }, error => {
      this.fetchLocationAndExecuteCallback(source, defaultCallBack);
    });    
  }
  private fetchLocationAndExecuteCallback(source: string, defaultCallBack: any) {
    this.getLatLng().subscribe(
      success => {
        const fromLat = success.latitude;
        const fromLng = success.longitude;
        if (fromLat && fromLng) {
          const query = ApiRoutes.REVERSE_GEOCODE + '?lat=' + fromLat.toString() + '&lng=' + fromLng.toString() + '&radius=24901&amenities=Mobile-Order&count=1&showClosedUnit=False';
          this.proxy.httpGet(query).subscribe(
            success => {
              const locations = <LocationsModel>success.body.valueOf();
              const toLat = locations.locations[0].latitude;
              const toLng = locations.locations[0].longitude;
              const crowFliesDistance = this.locationTrackingService.calcCrowInMiles(fromLat, fromLng, toLat, toLng);
              if (crowFliesDistance && crowFliesDistance > 15) {
                this.showLocationNotAvailableModal(source);
              } else {
                defaultCallBack();
              }
            },
            error => {
              defaultCallBack();
            }
          );
        } else {
          defaultCallBack();
        }

      }, error => {
        defaultCallBack();
      }
    );
  }

  private showLocationNotAvailableModal(source: string) {
    setTimeout(() => {
      this.confirmModalService.show(
        'Online Ordering isn\'t available at this location ',
        '',
        'We\'ve found no available restaurants within 15 miles.',
        'Continue with Order',
        '',
        'Cancel',
        true,
        source,
        '',
        'locationCancel'
      );
    }, 300);
  }
  public updateOpenClosedDisplayStatusForLocations(locations: LocationsModel) {
    locations?.locations?.forEach(loc => {
          this.updateOpenClosedDisplayStatus(loc);      
    });
  }
  public updateOpenClosedDisplayStatus(location: LocationsEntity) {    
    const pickupCapabilities = location.capabilities?.filter(c => LocationConstants.PickupCapabilities.includes(c));
    if(pickupCapabilities?.length == 1 && !this.pickupTypeMetaData.some(m => m.amenities.includes(pickupCapabilities[0]) && (m.pickupType === PickupType.Delivery || m.pickupType === PickupType.TakeOut))) {
      location.openCloseDisplayStatus = `${location.openStatus} ${location.openInfo ?? ''} (${this.pickupTypeMetaData.find(p => p.amenities.includes(pickupCapabilities[0])).text} Only)`;
    } else {
      location.openCloseDisplayStatus = `${location.openStatus} ${location.openInfo ?? ''}`;
    }    
  }
}
